import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, Gift, Truck, Shield, Zap, Check, X, AlertCircle, Lock, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import flashOfferService from '../services/flashOfferService';
import { offerRestrictionService } from '../services/membershipService';
import { useAuth } from '../hooks/useAuth';

// Styled Components
const Section = styled.section`
  padding: 4rem 1rem;
  background: linear-gradient(135deg, #1a1a2e 0%, #0f0f23 100%);
  color: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 50%, rgba(147, 51, 234, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 80rem;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  h2 {
    font-size: clamp(2rem, 5vw, 3rem);
    font-weight: 700;
    color: white;
    margin-bottom: 1.5rem;
  }
  
  p {
    font-size: clamp(1rem, 2vw, 1.25rem);
    color: rgba(255, 255, 255, 0.8);
    max-width: 48rem;
    margin: 0 auto;
  }
`;

const CountdownBanner = styled.div`
  background: linear-gradient(135deg, #ef4444, #dc2626);
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 3rem;
  text-align: center;
  box-shadow: 0 10px 25px rgba(239, 68, 68, 0.3);
  
  .countdown-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    
    .icon {
      width: 1.5rem;
      height: 1.5rem;
    }
  }
  
  .countdown-display {
    display: flex;
    justify-content: center;
    gap: 1rem;
    
    .time-unit {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 0.5rem;
      padding: 1rem;
      min-width: 4rem;
      
      .time-value {
        font-size: 2rem;
        font-weight: 700;
        display: block;
      }
      
      .time-label {
        font-size: 0.75rem;
        text-transform: uppercase;
        opacity: 0.8;
      }
    }
  }
`;

const OffersGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const OfferCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(0.5rem);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 1rem;
  padding: 2rem;
  position: relative;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    background: rgba(255, 255, 255, 0.15);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }
  
  &.featured {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(147, 51, 234, 0.2));
    border-color: #3b82f6;
    
    &::before {
      content: '${props => props.badge || 'POPULAIRE'}';
      position: absolute;
      top: -0.75rem;
      left: 50%;
      transform: translateX(-50%);
      background: #3b82f6;
      color: white;
      padding: 0.25rem 1rem;
      border-radius: 9999px;
      font-size: 0.75rem;
      font-weight: 600;
    }
  }
  
  &.flash {
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(245, 158, 11, 0.2));
    border-color: #ef4444;
    
    &::before {
      content: '${props => props.badge || 'FLASH'}';
      position: absolute;
      top: -0.75rem;
      left: 50%;
      transform: translateX(-50%);
      background: linear-gradient(135deg, #ef4444, #f59e0b);
      color: white;
      padding: 0.25rem 1rem;
      border-radius: 9999px;
      font-size: 0.75rem;
      font-weight: 600;
      animation: pulse 2s infinite;
    }
  }
  
  @keyframes pulse {
    0%, 100% { transform: translateX(-50%) scale(1); }
    50% { transform: translateX(-50%) scale(1.05); }
  }
  
  .offer-header {
    text-align: center;
    margin-bottom: 1.5rem;
    
    .offer-icon {
      width: 3rem;
      height: 3rem;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1rem;
      
      .icon {
        width: 1.5rem;
        height: 1.5rem;
        color: white;
      }
    }
    
    .offer-title {
      font-size: 1.5rem;
      font-weight: 700;
      margin-bottom: 0.5rem;
    }
    
    .offer-subtitle {
      color: rgba(255, 255, 255, 0.8);
      font-size: 0.875rem;
    }
  }
  
  .offer-price {
    text-align: center;
    margin-bottom: 1.5rem;
    
    .price-original {
      text-decoration: line-through;
      color: rgba(255, 255, 255, 0.6);
      font-size: 1rem;
    }
    
    .price-current {
      font-size: 2.5rem;
      font-weight: 900;
      color: #10b981;
      margin: 0.5rem 0;
    }
    
    .price-save {
      background: rgba(16, 185, 129, 0.2);
      color: #10b981;
      padding: 0.25rem 0.75rem;
      border-radius: 9999px;
      font-size: 0.875rem;
      font-weight: 600;
      display: inline-block;
    }
  }
  
  .offer-features {
    list-style: none;
    padding: 0;
    margin: 1.5rem 0;
    
    .feature-item {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 0.75rem;
      font-size: 0.875rem;
      
      .icon {
        width: 1rem;
        height: 1rem;
        color: #10b981;
        flex-shrink: 0;
      }
    }
  }
  
  .offer-stock {
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    text-align: center;
    font-size: 0.875rem;
    margin-bottom: 1.5rem;
  }
  
  .flash-timer {
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(245, 158, 11, 0.2));
    color: #f59e0b;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    text-align: center;
    font-size: 0.875rem;
    margin-bottom: 1.5rem;
    font-weight: 600;
    animation: pulse 2s infinite;
  }
  
  .membership-badge {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
    margin-bottom: 1rem;
    
    &.lite {
      background: rgba(59, 130, 246, 0.2);
      color: #3b82f6;
    }
    
    &.premium {
      background: linear-gradient(135deg, rgba(147, 51, 234, 0.2), rgba(236, 72, 153, 0.2));
      color: #9333ea;
      border: 1px solid rgba(147, 51, 234, 0.3);
    }
    
    &.flash {
      background: linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(245, 158, 11, 0.2));
      color: #ef4444;
      animation: pulse 2s infinite;
    }
  }
  
  .offer-button {
    width: 100%;
    background: #007AFF;
    color: white;
    font-weight: 600;
    padding: 0.75rem 2rem;
    border-radius: 0.5rem;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background: #0056CC;
      transform: scale(1.02);
    }

    &:disabled {
      background: #6b7280;
      cursor: not-allowed;
      transform: none;
      
      &:hover {
        background: #6b7280;
        transform: none;
      }
    }
  }

  .offer-button.disabled {
    background: #6b7280;
    cursor: not-allowed;
    opacity: 0.7;
    
    &:hover {
      background: #6b7280;
      transform: none;
    }
  }

  .offer-restriction {
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #ef4444;
    padding: 0.75rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .icon {
      flex-shrink: 0;
    }
  }

  .offer-success {
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.3);
    color: #10b981;
    padding: 0.75rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .icon {
      flex-shrink: 0;
    }
  }

  .offer-card.disabled {
    opacity: 0.7;
    cursor: not-allowed;
    
    &:hover {
      transform: none;
      background: rgba(255, 255, 255, 0.1);
    }
  }
`;

const TrustBadges = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-top: 4rem;
  
  .badge {
    text-align: center;
    
    .badge-icon {
      width: 3rem;
      height: 3rem;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1rem;
      
      .icon {
        width: 1.5rem;
        height: 1.5rem;
        color: white;
      }
    }
    
    .badge-title {
      font-weight: 600;
      margin-bottom: 0.5rem;
    }
    
    .badge-text {
      font-size: 0.875rem;
      color: rgba(255, 255, 255, 0.8);
    }
  }
`;

const OffersSection = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [flashOffers, setFlashOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 59,
  });
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [stockCount, setStockCount] = useState(3);
  const [offerRestrictions, setOfferRestrictions] = useState({});
  const [checkingRestrictions, setCheckingRestrictions] = useState(false);

  // Charger les offres flash depuis l'API
  useEffect(() => {
    const loadFlashOffers = async () => {
      try {
        setLoading(true);
        const data = await flashOfferService.getAllFlashOffers();
        setFlashOffers(data);
      } catch (err) {
        console.error('Error loading flash offers:', err);
      } finally {
        setLoading(false);
      }
    };

    loadFlashOffers();
  }, []);

  // Vérifier les restrictions d'offres pour les utilisateurs connectés
  useEffect(() => {
    const checkOfferRestrictions = async () => {
      if (!isAuthenticated || flashOffers.length === 0) return;
      
      try {
        setCheckingRestrictions(true);
        
        // Convertir les offres flash de l'API au format attendu
        const flashOffersFormatted = flashOffers.map(offer => ({
          id: offer.id,
          title: offer.title,
          discount: `-${offer.discount_percentage}%`,
          originalPrice: Number(offer.original_price),
          discountedPrice: Number(offer.discounted_price),
          description: offer.description,
          includes: offer.includes,
          icon: Clock,
          popular: false,
          stock: offer.stock,
          membership: 'flash',
          timeLeft: flashOfferService.getRemainingTime(offer.expires_at),
          badge: 'FLASH',
          product_slug: offer.product_slug
        }));
        
        // Combiner toutes les offres
        const allOffers = [...staticOffers, ...flashOffersFormatted];
        
        // Vérifier les restrictions pour toutes les offres
        const restrictions = await offerRestrictionService.checkMultipleOffersAvailability(allOffers);
        
        // Convertir en objet pour un accès facile
        const restrictionsMap = {};
        restrictions.forEach(restriction => {
          restrictionsMap[restriction.offerId] = restriction;
        });
        
        setOfferRestrictions(restrictionsMap);
      } catch (error) {
        console.error('Error checking offer restrictions:', error);
      } finally {
        setCheckingRestrictions(false);
      }
    };

    checkOfferRestrictions();
  }, [isAuthenticated, flashOffers]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        const totalSeconds = prev.hours * 3600 + prev.minutes * 60 + prev.seconds - 1;
        
        if (totalSeconds <= 0) {
          return { hours: 0, minutes: 0, seconds: 0 };
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Simuler le décompte du stock
  useEffect(() => {
    const stockTimer = setInterval(() => {
      setStockCount(prev => {
        if (prev <= 1) {
          return 8; // Reset pour demo
        }
        return prev - 1;
      });
    }, 45000);

    return () => clearTimeout(stockTimer);
  }, [stockCount]);

  // Offres statiques (Club INZA)
  const staticOffers = [
    {
      id: 'club-lite',
      title: 'Club INZA Lite',
      discount: 'GRATUIT',
      originalPrice: 0,
      discountedPrice: 0,
      description: 'Rejoignez le club gratuitement',
      includes: [
        'Accès aux offres flash exclusives',
        'Programme de fidélité par points',
        'Newsletter VIP',
        'Notifications prioritaires',
        'Anniversaire bonus (500 points)'
      ],
      icon: Gift,
      popular: true,
      stock: 'Illimité',
      membership: 'lite',
      badge: 'POPULAIRE'
    },
    {
      id: 'club-premium',
      title: 'Club INZA Premium',
      discount: '-30% sur premier achat',
      originalPrice: 9900,
      discountedPrice: 9900,
      description: '9 900 FCFA/mois',
      includes: [
        'Tout le Club Lite +',
        'Livraison gratuite illimitée',
        'Garantie étendue automatique',
        'Support prioritaire 24/7',
        'Reprise avec bonus +20%',
        'Offres VIP privées',
        'Cashback 5% sur tous les achats'
      ],
      icon: Shield,
      popular: true,
      stock: 'Illimité',
      membership: 'premium',
      badge: 'MEILLEUR VALUE'
    }
  ];

  // Convertir les offres flash de l'API au format attendu
  const flashOffersFormatted = flashOffers.map(offer => ({
    id: offer.id,
    title: offer.title,
    discount: `-${offer.discount_percentage}%`,
    originalPrice: Number(offer.original_price),
    discountedPrice: Number(offer.discounted_price),
    description: offer.description,
    includes: offer.includes,
    icon: Clock,
    popular: false,
    stock: offer.stock,
    membership: 'flash',
    timeLeft: flashOfferService.getRemainingTime(offer.expires_at),
    badge: 'FLASH',
    product_slug: offer.product_slug
  }));

  // Combiner toutes les offres
  const allOffers = [...staticOffers, ...flashOffersFormatted];

  const formatTimeLeft = (timeObj) => {
    if (typeof timeObj === 'string') {
      return timeObj; // Pour les offres flash de l'API
    }
    return `${timeObj.hours}h ${timeObj.minutes}m ${timeObj.seconds}s`;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const formatTime = (value) => {
    return value.toString().padStart(2, '0');
  };

  return (
    <Section id="offers">
      <Container>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Header>
            <h2>Offres exclusives</h2>
            <p>
              Profitez de nos promotions limitées et économisez sur votre nouvel iPhone.
            </p>
          </Header>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'white' }}>
            Chargement des offres flash...
          </div>
        )}

        {/* Offers Grid */}
        {!loading && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
          <OffersGrid>
            {allOffers.map((offer, index) => {
              const restriction = offerRestrictions[offer.id] || { isAvailable: true };
              const isDisabled = !restriction.isAvailable;
              
              return (
                <motion.div
                  key={offer.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: isDisabled ? 0 : -8 }}
                >
                  <OfferCard 
                    className={`
                      ${offer.popular ? 'featured' : (offer.membership === 'flash' ? 'flash' : '')}
                      ${isDisabled ? 'disabled' : ''}
                    `}
                    badge={offer.badge}
                    onClick={() => !isDisabled && setSelectedOffer(selectedOffer === offer.id ? null : offer.id)}
                  >
                    <div className="offer-header">
                      <div className="offer-icon">
                        <offer.icon className="icon" />
                      </div>
                      <h3 className="offer-title">{offer.title}</h3>
                      <p className="offer-subtitle">{offer.description}</p>
                      <div className={`membership-badge ${offer.membership}`}>
                        {offer.membership === 'lite' && 'GRATUIT'}
                        {offer.membership === 'premium' && 'PREMIUM'}
                        {offer.membership === 'flash' && 'FLASH DEAL'}
                      </div>
                    </div>

                    <div className="offer-price">
                      {offer.originalPrice > 0 && (
                        <div className="price-original">{offer.originalPrice.toLocaleString()} FCFA</div>
                      )}
                      <div className="price-current">
                        {offer.discountedPrice > 0 ? `${offer.discountedPrice.toLocaleString()} FCFA` : 'GRATUIT'}
                      </div>
                      <div className="price-save">{offer.discount}</div>
                    </div>

                    <ul className="offer-features">
                      {offer.includes.map((item, i) => (
                        <li key={i} className="feature-item">
                          <Check className="icon" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Messages de restriction */}
                    {isDisabled && restriction.reason && (
                      <div className="offer-restriction">
                        <Lock className="icon" size={16} />
                        <span>{restriction.reason}</span>
                      </div>
                    )}

                    {!isDisabled && isAuthenticated && (
                      <div className="offer-success">
                        <CheckCircle className="icon" size={16} />
                        <span>Disponible pour vous</span>
                      </div>
                    )}

                    {/* Timer pour les offres flash */}
                    {offer.membership === 'flash' && offer.timeLeft && (
                      <div className="flash-timer">
                        ⏰ Temps restant: {formatTimeLeft(offer.timeLeft)}
                      </div>
                    )}

                    {/* Stock limité */}
                    {offer.stock && typeof offer.stock === 'number' && (
                      <div className="offer-stock">
                        🚀 Seulement {offer.stock} en stock!
                      </div>
                    )}

                    {offer.stock && typeof offer.stock !== 'number' && (
                      <div className="offer-stock">
                        {offer.stock === 'Illimité' ? '✅ Disponible' : 'Disponible'}
                      </div>
                    )}

                    <button 
                      className={`offer-button ${isDisabled ? 'disabled' : ''}`}
                      disabled={isDisabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isDisabled) {
                          // Rediriger selon le type d'offre
                          if (offer.membership === 'lite') {
                            navigate('/signup?plan=lite');
                          } else if (offer.membership === 'premium') {
                            navigate('/signup?plan=premium');
                          } else if (offer.membership === 'flash' && offer.product_slug) {
                            navigate(`/product/${offer.product_slug}?offer=${offer.id}`);
                          }
                        }
                      }}
                    >
                      {isDisabled ? (
                        restriction.alreadySubscribed ? 'DÉJÀ ABONNÉ' : 
                        restriction.alreadyUsed ? 'DÉJÀ UTILISÉE' : 
                        'INDISPONIBLE'
                      ) : (
                        <>
                          {offer.membership === 'lite' && 'Rejoindre GRATUITEMENT'}
                          {offer.membership === 'premium' && 'S\'abonner (9 900 FCFA/mois)'}
                          {offer.membership === 'flash' && 'ACHETER MAINTENANT'}
                        </>
                      )}
                    </button>
                  </OfferCard>
                </motion.div>
              );
            })}
          </OffersGrid>
          </motion.div>
        )}

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <TrustBadges>
            <div className="badge">
              <div className="badge-icon">
                <Truck className="icon" />
              </div>
              <h4 className="badge-title">Livraison gratuite</h4>
              <p className="badge-text">Expédition sous 24h</p>
            </div>
            <div className="badge">
              <div className="badge-icon">
                <Shield className="icon" />
              </div>
              <h4 className="badge-title">Garantie 2 ans</h4>
              <p className="badge-text">Sérénité totale</p>
            </div>
            <div className="badge">
              <div className="badge-icon">
                <Zap className="icon" />
              </div>
              <h4 className="badge-title">Service Premium</h4>
              <p className="badge-text">Support 7j/7</p>
            </div>
          </TrustBadges>
        </motion.div>
      </Container>
    </Section>
  );
};

export default OffersSection;
